<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Items Classify</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <!-- Classify -->
    <div class="px-6 pt-3">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Chart of account</label>
          <select
            v-model="classifyAccountId"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required
          >
            <option value="" disabled>Chart of Accounts</option>
            <option v-for="acc in enabledChartAccounts" :key="acc.id" :value="String(acc.id)">
              {{ formatAccountLabel(acc) }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Category</label>
          <select
            v-model="selectedCategoryId"
            @change="loadUnclassifiedItems"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
          >
            <option value="" disabled>Categories</option>
            <option v-for="cat in categoriesWithAll" :key="cat.id" :value="String(cat.id)">
              {{ cat.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="px-6 pt-4">
      <div class="w-full flex justify-end mb-2">
        <input
          v-model="searchQueryUnclassified"
          type="text"
          class="w-full sm:w-[260px] px-2.5 pr-8 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          placeholder="Search by Name or Code"
        />
        <svg
          class="w-4 h-4 text-slate-400 -ml-6 mt-[7px] pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <div class="w-full max-lg:overflow-x-auto">
        <table class="w-full max-lg:min-w-[760px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in filteredUnclassifiedItems"
              :key="'uc-' + String(row?.id ?? '') + '-' + idx"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td>{{ idx + 1 }}</td>
              <td>{{ row.itemCode || '-' }}</td>
              <td>{{ row.itemName || '-' }}</td>
              <td>{{ row.category?.name || '-' }}</td>
              <td>{{ row.unit || '-' }}</td>
              <td class="actions">
                <input
                  class="w-4 h-4 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                  type="checkbox"
                  title="Select"
                  :checked="selectedItemIds.has(String(row.id))"
                  @change="toggleSelectUnclassified(row)"
                  :disabled="!canSelectRow"
                >
              </td>
            </tr>
            <tr v-if="filteredUnclassifiedItems.length === 0 && !isLoadingUnclassified">
              <td colspan="6" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                {{ unclassifiedTableEmptyText }}
              </td>
            </tr>
            <tr v-if="isLoadingUnclassified">
              <td colspan="6" style="text-align:center; color:#64748b; padding: 24px 15px; font-size: 0.75rem;">
                Loading items…
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-3 flex justify-center">
        <button
          type="button"
          @click="saveSelected"
          :disabled="!canSaveSelected"
          class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save
        </button>
      </div>
    </div>

    <!-- View classified -->
    <div class="px-6 pt-8">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Chart of Account</label>
          <select
            v-model="viewAccountId"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
          >
            <option value="" selected disabled>Chart of Accounts</option>
            <option v-for="acc in chartAccounts" :key="acc.id" :value="String(acc.id)">
              {{ formatAccountLabel(acc) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="px-6 pt-4 pb-6">
      <div class="w-full flex justify-end mb-2">
        <input
          v-model="searchQueryClassified"
          type="text"
          class="w-full sm:w-[260px] px-2.5 pr-8 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          placeholder="Search by Name or Code"
        />
        <svg
          class="w-4 h-4 text-slate-400 -ml-6 mt-[7px] pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <div class="w-full max-lg:overflow-x-auto">
        <table class="w-full max-lg:min-w-[760px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in filteredClassifiedItems"
              :key="'cf-' + String(row?.itemId ?? '') + '-' + idx"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td>{{ idx + 1 }}</td>
              <td>{{ row.itemCode || '-' }}</td>
              <td>{{ row.itemName || '-' }}</td>
              <td>{{ row.categoryName || '-' }}</td>
              <td>{{ row.unit || '-' }}</td>
              <td class="actions">
                <input
                  class="w-4 h-4 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                  type="checkbox"
                  title="Classified"
                  :checked="!pendingRemovalIds.has(String(row.itemId))"
                  @change="togglePendingRemoval(row)"
                  :disabled="!viewAccountId"
                >
              </td>
            </tr>
            <tr v-if="filteredClassifiedItems.length === 0 && !isLoadingClassified">
              <td colspan="6" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                {{ classifiedTableEmptyText }}
              </td>
            </tr>
            <tr v-if="isLoadingClassified">
              <td colspan="6" style="text-align:center; color:#64748b; padding: 24px 15px; font-size: 0.75rem;">
                Loading classified items…
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="mt-3 flex justify-center">
        <button
          type="button"
          @click="saveClassifiedChanges"
          :disabled="!canSaveClassifiedChanges"
          class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'
import FinanceToast from '../FinanceToast.vue'

const currentUser = computed(() => store.getters.currentUser)

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddItemsClassify = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Items Classify'), 'Add')
})

const canEditItemsClassify = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Items Classify'), 'Edit')
})

const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch?.id || null)

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const toast = ref({ show: false, message: '', variant: 'success' })
let toastTimer = null
function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.value = { show: true, message, variant }
  const duration = variant === 'error' ? 5000 : 1600
  toastTimer = window.setTimeout(() => {
    toast.value = { show: false, message: '', variant: 'success' }
    toastTimer = null
  }, duration)
}

const chartAccounts = ref([])
const enabledChartAccounts = computed(() =>
  chartAccounts.value.filter((a) => a.enabled === true || a.enabled === 1)
)
const categories = ref([])
const categoriesWithAll = computed(() => [{ id: 'all', name: 'All' }, ...categories.value])

const unclassifiedItems = ref([])
const isLoadingUnclassified = ref(false)

const classifyAccountId = ref('')
const selectedCategoryId = ref('')
const selectedItemIds = ref(new Set())

const viewAccountId = ref('')
const classifiedItems = ref([])
const isLoadingClassified = ref(false)
const pendingRemovalIds = ref(new Set())

const searchQueryUnclassified = ref('')
const searchQueryClassified = ref('')

const filteredUnclassifiedItems = computed(() => {
  const query = String(searchQueryUnclassified.value || '').trim().toLowerCase()
  const list = unclassifiedItems.value
  if (!query) return list
  return list.filter(
    (row) =>
      String(row?.itemName || '').toLowerCase().includes(query) ||
      String(row?.itemCode || '').toLowerCase().includes(query)
  )
})

const filteredClassifiedItems = computed(() => {
  const query = String(searchQueryClassified.value || '').trim().toLowerCase()
  const list = classifiedItems.value
  if (!query) return list
  return list.filter(
    (row) =>
      String(row?.itemName || '').toLowerCase().includes(query) ||
      String(row?.itemCode || '').toLowerCase().includes(query)
  )
})

function formatAccountLabel(acc) {
  const code = acc?.code ? String(acc.code) : ''
  const name = acc?.accountName ? String(acc.accountName) : ''
  return code && name ? `${code} - ${name}` : name || code || 'Account'
}

async function loadChartAccounts() {
  try {
    const res = await apiFetch('/finance/chart-of-account')
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load chart of accounts.', 'error')
      chartAccounts.value = []
      return
    }
    const parsed = await res.json().catch(() => [])
    const list = Array.isArray(parsed) ? parsed : []
    chartAccounts.value = list
        chartAccounts.value.sort((a, b) => a.accountName.localeCompare(b.accountName))
    
  } catch {
    showToast('Failed to load chart of accounts.', 'error')
    chartAccounts.value = []
  }
}

async function loadCategories() {
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    const res = await apiFetch(`/categories?${params.toString()}`)
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    categories.value = Array.isArray(data) ? data : []
    categories.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch {
    categories.value = []
  }
}

async function loadUnclassifiedItems() {
  selectedItemIds.value = new Set()
  searchQueryUnclassified.value = ''
  const catId = String(selectedCategoryId.value || '')
  if (!catId) {
    unclassifiedItems.value = []
    return
  }
  isLoadingUnclassified.value = true
  unclassifiedItems.value = []
  try {
    const q = catId === 'all' ? 'all' : catId
    const res = await apiFetch(`/finance/items-classify/unclassified?categoryId=${encodeURIComponent(q)}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load items.', 'error')
      return
    }
    const data = await res.json().catch(() => [])
    unclassifiedItems.value = Array.isArray(data) ? data : []
     unclassifiedItems.value.sort((a, b) => a.itemName.localeCompare(b.itemName))

  } catch {
    showToast('Failed to load items.', 'error')
    unclassifiedItems.value = []
  } finally {
    isLoadingUnclassified.value = false
  }
}

const canSelectRow = computed(() => !!classifyAccountId.value && !!selectedCategoryId.value)

function toggleSelectUnclassified(row) {
  if (!canSelectRow.value) return
  const id = String(row.id)
  const next = new Set(selectedItemIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedItemIds.value = next
}

const unclassifiedEmptyText = computed(() => {
  if (!classifyAccountId.value) return 'Select chart of account first.'
  if (!selectedCategoryId.value) return 'Select category to load items.'
  if (isLoadingUnclassified.value) return ''
  return 'No unclassified items available.'
})

const unclassifiedTableEmptyText = computed(() => {
  const q = String(searchQueryUnclassified.value || '').trim()
  if (q && unclassifiedItems.value.length > 0) return 'No items match your search.'
  return unclassifiedEmptyText.value
})

const canSaveSelected = computed(() => {
  if (!canAddItemsClassify.value) return false
  return !!classifyAccountId.value && selectedItemIds.value.size > 0 && !isLoadingUnclassified.value
})

async function saveSelected() {
  if (!canAddItemsClassify.value) return
  if (!canSaveSelected.value) return
  const accId = String(classifyAccountId.value)
  const itemIds = [...selectedItemIds.value].map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n))
  if (itemIds.length === 0) return

  try {
    const res = await apiFetch('/finance/items-classify/assign', {
      method: 'POST',
      body: JSON.stringify({ chartOfAccountId: parseInt(accId, 10), itemIds }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save classification.', 'error')
      return
    }
    selectedItemIds.value = new Set()
    showToast('Items classified successfully.', 'success')
    await loadUnclassifiedItems()
    if (viewAccountId.value === accId) {
      await loadClassifiedForView()
    }
  } catch {
    showToast('Failed to save classification.', 'error')
  }
}

async function loadClassifiedForView() {
  const accId = String(viewAccountId.value || '')
  if (!accId) {
    classifiedItems.value = []
    searchQueryClassified.value = ''
    return
  }
  isLoadingClassified.value = true
  classifiedItems.value = []
  try {
    const res = await apiFetch(`/finance/items-classify/classified/${encodeURIComponent(accId)}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load classified items.', 'error')
      return
    }
    const data = await res.json().catch(() => [])
    classifiedItems.value = Array.isArray(data) ? data : []
     classifiedItems.value.sort((a, b) => a.itemName.localeCompare(b.itemName))


  } catch {
    showToast('Failed to load classified items.', 'error')
  } finally {
    isLoadingClassified.value = false
  }
}

const canSaveClassifiedChanges = computed(() => {
  if (!canEditItemsClassify.value) return false
  if (!viewAccountId.value) return false
  if (pendingRemovalIds.value.size === 0) return false
  const existingIds = new Set(classifiedItems.value.map((x) => String(x.itemId)))
  for (const id of pendingRemovalIds.value) {
    if (existingIds.has(id)) return true
  }
  return false
})

const classifiedEmptyText = computed(() => {
  if (!viewAccountId.value) return 'Select chart of account to view classified items.'
  if (isLoadingClassified.value) return ''
  return 'No classified items available.'
})

const classifiedTableEmptyText = computed(() => {
  const q = String(searchQueryClassified.value || '').trim()
  if (q && classifiedItems.value.length > 0) return 'No items match your search.'
  return classifiedEmptyText.value
})

function togglePendingRemoval(row) {
  const accId = String(viewAccountId.value || '')
  if (!accId) return
  const id = String(row.itemId)
  const next = new Set(pendingRemovalIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  pendingRemovalIds.value = next
}

async function saveClassifiedChanges() {
  if (!canEditItemsClassify.value) return
  if (!canSaveClassifiedChanges.value) return
  const accId = String(viewAccountId.value || '')
  if (!accId) return
  const itemIds = [...pendingRemovalIds.value]
    .map((id) => parseInt(id, 10))
    .filter((n) => Number.isFinite(n))
  if (itemIds.length === 0) return

  try {
    const res = await apiFetch('/finance/items-classify/remove', {
      method: 'POST',
      body: JSON.stringify({ chartOfAccountId: parseInt(accId, 10), itemIds }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save changes.', 'error')
      return
    }
    pendingRemovalIds.value = new Set()
    showToast('Classified changes saved.', 'success')
    await loadClassifiedForView()
    await loadUnclassifiedItems()
  } catch {
    showToast('Failed to save changes.', 'error')
  }
}

watch(viewAccountId, () => {
  pendingRemovalIds.value = new Set()
  searchQueryClassified.value = ''
  loadClassifiedForView()
})

onMounted(async () => {
  await Promise.all([loadChartAccounts(), loadCategories()])
})
</script>

<style scoped>
/* match menuListPage.vue table + actions + toggle styles */
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

</style>

